// import { debouncedWatch } from "@vueuse/core";
import { ref, Ref, computed } from "vue";

interface SearchFilter<T> {
  searchTerm: Ref<string>;
  filteredItems: Ref<T[]>;
}

/**
 * This hook simplify the usage of filtering a list of item.
 * Usage:
 *
 * const { searchTerm, filteredItems: filteredUsers } = useSearchFilter(users, ['firstName', 'lastName'])
 *
 * Updating the searchTerm will updated the filterItems.
 * The algorithm will search the value of searchTerm in all property values.
 *
 * You can pass a function if you need more powerfulness. The following example is equivalent of the previous example:
 *
 *  const { searchTerm, filteredItems: filteredUsers } = useSearchFilter(users, (lowerSearchTerm, { firstName, lastName }) =>
 *     (firstName || "").toLowerCase().includes(lowerSearchTerm) ||
 *     (lastName || "").toLowerCase().includes(lowerSearchTerm) ||
 *     (description || "").toLowerCase().includes(lowerSearchTerm) ||
 *  );
 *
 * @param {A reactive array} items
 * @param {A prop names array or a function} propsArrayOrFilterFn
 *
 * @returns { searchTerm, filteredItems }
 */
export default function useSearchFilter<T>(
  items: Ref<T[]>,
  propsArrayOrFilterFn: string[] | ((lowerSearchTerm: string, item: T) => boolean)
): SearchFilter<T> {
  const searchTerm = ref("");

  let propNames: string[];
  let filterFn: (lowerSearchTerm: string, item: T) => boolean;
  if (Array.isArray(propsArrayOrFilterFn)) {
    propNames = propsArrayOrFilterFn;
  } else {
    filterFn = propsArrayOrFilterFn;
  }

  const filteredItems = computed(() => {
    const lowerSearchTerm = (searchTerm.value || "").toLowerCase();

    if (lowerSearchTerm) {
      if (propNames) {
        return items.value.filter((item) =>
          propNames.some((propName) => `${item[propName as never] || ""}`.toLowerCase().includes(lowerSearchTerm))
        );
      }
      return items.value.filter((item) => filterFn(lowerSearchTerm, item));
    }
    return items.value;
  });

  /**
   * Init a first time the filtered items before they get into the debounced watch
   * *This is an optimization for large lists of elements to filter
   */
  // const filteredItems = ref();
  // filteredItems.value = getFilteredItems();

  // debouncedWatch(
  //   [searchTerm, items],
  //   () => {
  //     filteredItems.value = getFilteredItems();
  //   },
  //   { debounce: 500, immediate: true }
  // );

  return {
    searchTerm,
    filteredItems,
  };
}
